import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FormActionBar, WindowContent, toast } from '@/shared/ui';
import { cn } from '@/shared/lib/utils';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { getGetProductsStationQueryKey } from '@/api/generated/products-stations/products-stations';
import { STATION_LIST_KEY } from '../internal/stationsQueryKeys';
import { useStationForm } from './useStationForm';
import { usePlaceEditor } from './usePlaceEditor';
import { StationIdentityPanel } from './components/StationIdentityPanel';
import { StationDetailsPanel } from './components/StationDetailsPanel';
import { StationMapPanel } from './components/StationMapPanel';
import type { StationFormValues } from './stationFormSchema';

export interface StationDetailViewProps {
  layerId: string;
  stationId?: number;
  initialValues: StationFormValues;
}

/**
 * Orchestrátor detailu stanice — drží formulář (`useStationForm`) a editor míst (`usePlaceEditor`),
 * skládá panely (identita + nástupní místa + mapa) a sdílenou lištu akcí v patičce.
 */
export function StationDetailView({ layerId, stationId, initialValues }: StationDetailViewProps) {
  const { t } = useTranslation('products');
  const navigator = useNavigator();
  const queryClient = useQueryClient();
  const isEdit = stationId != null;

  const close = useCallback(() => {
    void navigator.closeLayer(layerId);
  }, [navigator, layerId]);

  const { form, submit } = useStationForm({
    stationId,
    initialValues,
    onSaved: (id) => {
      // Detail (Orval klíč) — aby se po znovuotevření načetla čerstvá data; seznam (prefix klíče
      // ListPage configu). Viz `frontend/GUIDELINES.md` → „Detail stránky — čerstvost dat".
      void queryClient.invalidateQueries({ queryKey: getGetProductsStationQueryKey(id) });
      void queryClient.invalidateQueries({ queryKey: [STATION_LIST_KEY] });
      toast({ severity: 'success', description: t('station.toast.saved') });
    },
    onError: () => {
      toast({
        severity: 'error',
        title: t('station.toast.errorTitle'),
        description: t('station.toast.saveError'),
      });
    },
  });

  const editor = usePlaceEditor(form);

  const title = isEdit ? t('station.titleEdit') : t('station.titleCreate');

  return (
    <WindowContent
      as="form"
      scroll={false}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      footer={
        <form.Subscribe
          selector={(state) => ({
            isDirty: state.isDirty,
            canSubmit: state.canSubmit,
            isSubmitting: state.isSubmitting,
          })}
        >
          {({ isDirty, canSubmit, isSubmitting }) => (
            <FormActionBar
              onBack={close}
              onSave={submit}
              saving={isSubmitting}
              disabled={!canSubmit}
              statusNote={
                <>
                  <span
                    className={cn(
                      'mr-phx-2 inline-block h-1.5 w-1.5 rounded-full',
                      isDirty ? 'bg-phx-warning' : 'bg-phx-text-faint'
                    )}
                  />
                  {isDirty ? t('station.status.unsaved') : t('station.status.clean')}
                </>
              }
            />
          )}
        </form.Subscribe>
      }
    >
      {/* Vlastní 2-sloupcový split ve fill oblasti okna */}
      <div className="gap-phx-3 flex h-full min-h-0">
        {/* Detail (~500px) */}
        <div className="gap-phx-3 flex min-h-0 w-[500px] shrink-0 flex-col overflow-y-auto">
          <StationIdentityPanel
            form={form}
            title={title}
            onSetCoordsFromMap={() => editor.setMapMode({ kind: 'station-coords' })}
          />
          <StationDetailsPanel form={form} editor={editor} />
        </div>

        {/* Mapa (zbytek šířky) */}
        <StationMapPanel form={form} editor={editor} />
      </div>
    </WindowContent>
  );
}
